import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ChartHeader from 'components/charts/ChartHeader';
import CompositeFilter, { ANALYSIS_FILTERS, getActiveFilters } from 'components/charts/CompositeFilter';
import DataCards from 'components/charts/DataCards';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import PieChartV2 from 'components/charts/chartJS/PieChart';
import { chartColors } from 'components/charts/chartJS/utils';
import { Divider } from 'components/charts/styled/Chart.styled';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import Typography from 'components/global/Typography';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';

import useBrandManagerFilter from 'hooks/useBrandManagerFilter';
import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';
import useLocale from 'hooks/useLocale';

import { REPORT } from 'services/consts';

import { colorCombinations } from 'utils/colors';

const SalesTab = () => {
	const { Currency } = useLocale();
	const filtersList = [
		{
			...ANALYSIS_FILTERS.currency,
			filterValues: {
				...ANALYSIS_FILTERS.currency.filterValues,
				value: [Currency]
			}
		},
		ANALYSIS_FILTERS.destinationCountry,
		ANALYSIS_FILTERS.destinationCity,
		ANALYSIS_FILTERS.brand,
		ANALYSIS_FILTERS.salesChannel,
		ANALYSIS_FILTERS.paymentMethod
	];
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const { userType, assignedBrands } = useSelector((state) => state.user);
	const isBrandManager = userType === 'BrandManager';

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			from: filter?.start,
			to: filter?.end,
			filter: getActiveFilters(extraFilters)
		}),
		[filter, extraFilters]
	);

	return (
		<WidgetPageContainer>
			<Typography fontWeight={700} variant="subtitle1">
				filterSaleByDate
			</Typography>
			<div style={{ gridColumn: 'span 6' }}>
				<div style={{ display: 'flex', gap: '1rem' }}>
					<DateFilter defaultDay="last30Days" setValue={setFilter} />
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
						ref={setFilterMenuButtonRef}
						label="filters"
						variant="borderless"
						size="small"
						className="py-0"
					/>
					<CompositeFilter
						filtersMenuButtonRef={filtersMenuButtonRef}
						filtersList={extraFilters}
						setFiltersList={setExtraFilters}
					/>
				</div>
			</div>
			<WidgetWrapper widgetId={'orderValuesSummary'}>
				<OrderValuesSummarySales body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'shipmentPaymentType'}>
				<ShipmentPaymentType body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'ordersByCity'}>
				<OrdersByCity body={body} />
			</WidgetWrapper>
			<WidgetWrapper widgetId={'numberOfOrders'}>
				<NumberOfOrdersSales body={body} />
			</WidgetWrapper>
			{!isBrandManager && (
				<WidgetWrapper widgetId={'avgBasketSize'}>
					<Divider />
					<ChartHeader
						title={'sales.avgBasketSize'}
						showDivider={false}
						subtitle={'sales.avgBasketSizeSubtitle'}
						gutterBottom
					/>
					<AvgBasketSize body={body} />
				</WidgetWrapper>
			)}
		</WidgetPageContainer>
	);
};

export const OrderValuesSummarySales = ({ widgetName, body }) => {
	const cardsConfig = [
		{
			accessor: 'newReceived',
			label: 'newReceived',
			customIcon: 'fa-regular-box-circle-arrow-down',
			tooltip: 'dashboard.newReceived.description',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-down-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-down-color)'
		},
		{
			accessor: 'processing',
			label: 'processing',
			customIcon: 'fa-regular-loader-circle-bolt',
			tooltip: 'dashboard.processing.description',
			backgroundColor: 'var(--fa-regular-loader-circle-bolt-bgColor)',
			color: 'var(--fa-regular-loader-circle-bolt-color)'
		},
		{
			accessor: 'delivered',
			label: 'delivered',
			customIcon: 'fa-regular-box-circle-check',
			tooltip: 'dashboard.delivered.description',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'returned',
			label: 'returned',
			customIcon: 'fa-regular-box-circle-arrow-left',
			tooltip: 'dashboard.returned.description',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)'
		},
		{
			accessor: 'canceled',
			label: 'canceled',
			customIcon: 'fa-regular-box-circle-xmark',
			tooltip: 'dashboard.canceled.description',
			backgroundColor: 'var(--fa-regular-box-circle-xmark-bgColor)',
			color: 'var(--fa-regular-box-circle-xmark-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_TODAY_ACTIVITY,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader title={widgetName || 'orderValuesSummary'} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

const pieChartAfterFetch = (data) => {
	const labels = data.map((item) => item.label);
	const datasets = [{ data: data.map((item) => item.value), ...chartColors }];
	return { labels, datasets };
};

export const ShipmentPaymentType = ({ widgetName, body }) => {
	const shipmentPaymentTypeAfterFetch = (data) => {
		const labels = data.map((item) => `shipmentPaymentType.${item.label}`);
		const datasets = [{ data: data.map((item) => item.value), ...chartColors }];
		return { labels, datasets };
	};

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_FREE_VS_PAID_SHIPPING,
		body,
		parseData: shipmentPaymentTypeAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader
				title={widgetName || 'shipmentPaymentType'}
				titleTooltip={'analysis.shipmentPaymentTypeDescription'}
			/>
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

export const OrdersByCity = ({ widgetName, body }) => {
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_TOP_5_CITIES,
		body,
		parseData: pieChartAfterFetch
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={widgetName || 'ordersByCity'} titleTooltip={'analysis.ordersByCityDescription'} />
			<PieChartV2 data={data} loading={loading} />
		</Card>
	);
};

const barChartAfterFetch = (data, t) => {
	const labels = data.map((item) => item.xAxis);
	const datasets = [
		{
			label: t('avgBasketSize'),
			data: data.map((item) => item.yAxis),
			backgroundColor: colorCombinations[0].color,
			borderColor: colorCombinations[0].color
		}
	];
	return { labels, datasets };
};

export const NumberOfOrdersSales = ({ widgetName, body }) => {
	const { t } = useTranslation();

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_NUMBER_OF_ORDERS,
		body,
		parseData: (data) => barChartAfterFetch(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={widgetName || 'numberOfOrders'} titleTooltip={'analysis.numberOfOrdersDescription'} />
			<BarChartV2 xTitle="date" yTitle="numberOfOrders" data={data} loading={loading} />
		</Card>
	);
};

export const AvgBasketSize = ({ widgetName, body }) => {
	const { t } = useTranslation();

	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.SALES.GET_AVG_BASKET_SIZE,
		body,
		parseData: (data) => barChartAfterFetch(data, t)
	});

	return (
		<Card ref={ref}>
			<ChartHeader title={widgetName || 'avgBasketSize'} titleTooltip={'analysis.avgBasketSizeDescription'} />
			<BarChartV2 xTitle="date" yTitle="avgBasketSize" data={data} loading={loading} />
		</Card>
	);
};

export default SalesTab;
